import { queryOptions, useQuery } from '@tanstack/react-query';

import { ListRunbookActivities } from '@cast/types-apa';
import { useCompoundReducer } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const APA_RUNBOOK_ACTIVITIES_QUERY_KEY = 'apa-runbook-activities';

const getRunbookActivitiesQueryKey = ({
  clusterId,
  runbookId,
  runId,
  pageLimit,
  currentPage,
}: {
  clusterId?: string;
  runbookId?: string;
  runId?: string;
  pageLimit?: number;
  currentPage?: number;
}) =>
  [
    APA_RUNBOOK_ACTIVITIES_QUERY_KEY,
    clusterId,
    runbookId,
    runId,
    pageLimit,
    currentPage,
  ] as const;

type UseRunbookActivitiesQueryArgs = {
  runbookId?: string;
  clusterId?: string;
  runId?: string;
  pageLimit?: number;
  enabled?: boolean;
};

type PaginatedQueryState = {
  currentPage: number;
  currentCursor?: string;
  nextCursor?: string;
  visitedPages: string[];
};

const initialState: PaginatedQueryState = {
  currentPage: 1,
  currentCursor: '',
  visitedPages: [],
};

type ActivitiesDispatcher = {
  dataLoaded: (data: ListRunbookActivities) => void;
};

const getRunbookActivitiesQueryOptions = ({
  clusterId,
  runbookId,
  runId,
  pageLimit,
  enabled,
  organizationId,
  state,
  dispatcher,
}: {
  clusterId?: string;
  runbookId?: string;
  runId?: string;
  pageLimit: number;
  enabled: boolean;
  organizationId: string;
  state: PaginatedQueryState;
  dispatcher: ActivitiesDispatcher;
}) =>
  queryOptions({
    queryKey: getRunbookActivitiesQueryKey({
      clusterId,
      runbookId,
      runId,
      pageLimit,
      currentPage: state.currentPage,
    }),
    queryFn: async () => {
      const { data } = await apiClient.apa.getRunbookActivities({
        organizationId,
        clusterId: clusterId!,
        runbookId,
        runId,
        pageCursor: state.currentCursor,
        pageLimit: String(pageLimit),
      });

      dispatcher.dataLoaded(data);
      return data;
    },
    enabled:
      enabled && !!organizationId && !!clusterId && !!runbookId && !!runId,
  });

export const useRunbookActivitiesQuery = ({
  runbookId,
  clusterId,
  runId,
  pageLimit = 50,
  enabled = true,
}: UseRunbookActivitiesQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const { state, dispatcher } = useCompoundReducer(
    {
      dataLoaded: (state, data: ListRunbookActivities) => {
        return {
          ...state,
          nextCursor: data.nextPageCursor,
        };
      },
      fetchNextPage: (state) => {
        const currentCursor = state.currentCursor;
        const newPage = state.currentPage + 1;

        return {
          ...state,
          currentPage: newPage,
          visitedPages: state.visitedPages.concat(currentCursor!),
          currentCursor: state.nextCursor,
        };
      },
      fetchPreviousPage: (state) => {
        const newPage = state.currentPage - 1;
        const currentCursor = state.visitedPages[newPage - 1];

        return {
          ...state,
          currentPage: newPage,
          currentCursor,
          nextCursor: state.currentCursor,
        };
      },
    },
    initialState
  );

  const { data, isLoading, error, isFetching, refetch } = useQuery(
    getRunbookActivitiesQueryOptions({
      clusterId,
      runbookId,
      runId,
      pageLimit,
      enabled,
      organizationId: currentOrganization?.id ?? '',
      state,
      dispatcher,
    })
  );

  return {
    activities: data?.items || [],
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage: () => {
      dispatcher.fetchNextPage();
    },
    fetchPreviousPage: () => {
      dispatcher.fetchPreviousPage();
    },
    hasNextPage: !!state.nextCursor,
    hasPreviousPage: !!state.visitedPages?.[state.currentPage - 1],
    metadata: {
      currentPage: state.currentPage,
      totalCount: data?.totalCount,
      pageSize: pageLimit,
    },
  };
};
